import { isMunicipio, MUNICIPIOS_EDIFICACOES_HIERARQUICAS } from "../constants/municipios";
import {
  AnyData,
  ConfigCaracterizacao,
  ImobiliarioEdificacao,
} from "../interfaces";

export const TIPO_VINCULO_EDIFICACAO = {
  PRINCIPAL: 1,
  SUBUNIDADE: 2,
  COMPLEMENTAR: 3,
} as const;

export type TipoVinculoEdificacao =
  (typeof TIPO_VINCULO_EDIFICACAO)[keyof typeof TIPO_VINCULO_EDIFICACAO];

export interface EdificacaoVinculoNode {
  vinculo: ImobiliarioEdificacao;
  edificacao?: AnyData;
}

export interface EdificacaoSubunidadeNode extends EdificacaoVinculoNode {
  inscricaoSubunidade: string;
  complementares: EdificacaoVinculoNode[];
}

export interface EdificacaoUnidadePrincipalNode extends EdificacaoVinculoNode {
  inscricaoUnidade: string;
  subunidades: EdificacaoSubunidadeNode[];
  complementares: EdificacaoVinculoNode[];
}

export interface EdificacoesHierarchy {
  unidades: EdificacaoUnidadePrincipalNode[];
  orphans: EdificacaoVinculoNode[];
  naoVinculadas: AnyData[];
  semVinculos: EdificacaoVinculoNode[];
}

export function usaEdificacoesHierarquicasPorTipoVinculo(
  config?: ConfigCaracterizacao | null,
  municipio?: string | null
): boolean {
  if (config?.edificacoesHierarquicasPorTipoVinculo === true) return true;
  return isMunicipio(municipio, MUNICIPIOS_EDIFICACOES_HIERARQUICAS);
}

export function isInscricaoPreenchida(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== "";
}

export function normalizeTipoVinculo(value: unknown): TipoVinculoEdificacao | null {
  const tipo = Number(value);
  if (tipo === TIPO_VINCULO_EDIFICACAO.PRINCIPAL) return TIPO_VINCULO_EDIFICACAO.PRINCIPAL;
  if (tipo === TIPO_VINCULO_EDIFICACAO.SUBUNIDADE) return TIPO_VINCULO_EDIFICACAO.SUBUNIDADE;
  if (tipo === TIPO_VINCULO_EDIFICACAO.COMPLEMENTAR) return TIPO_VINCULO_EDIFICACAO.COMPLEMENTAR;
  return null;
}

export function isVinculoEdificacaoAtivo(vinculo?: unknown): boolean {
  const status = (vinculo as { status?: unknown } | null | undefined)?.status;
  return status === null || status === undefined || status === 1 || status === "1";
}

export function isVinculoEdificacaoDesativado(vinculo?: unknown): boolean {
  const status = (vinculo as { status?: unknown } | null | undefined)?.status;
  return status === 2 || status === "2";
}

export function getVinculosDaEdificacao(
  idEdificacao: string | number,
  vinculos: ImobiliarioEdificacao[],
): ImobiliarioEdificacao[] {
  return vinculos.filter((vinculo) => String(vinculo.id_edificacao) === String(idEdificacao));
}

export function isEdificacaoSemVinculoNoImobiliario(
  idEdificacao: string | number,
  vinculosDoImobiliario: ImobiliarioEdificacao[],
): boolean {
  return !vinculosDoImobiliario.some(
    (vinculo) => String(vinculo.id_edificacao) === String(idEdificacao),
  );
}

export function isEdificacaoDesativada(
  idEdificacao: string | number,
  vinculos: ImobiliarioEdificacao[],
): boolean {
  return getVinculosDaEdificacao(idEdificacao, vinculos).some(isVinculoEdificacaoDesativado);
}

export function shouldShowEdificacaoRioBranco(
  idEdificacao: string | number,
  vinculosDoImobiliario: ImobiliarioEdificacao[],
  options: { showDesativadas: boolean; showSemVinculos: boolean },
): boolean {
  if (options.showDesativadas) return true;

  const semVinculoNoImobiliario = isEdificacaoSemVinculoNoImobiliario(
    idEdificacao,
    vinculosDoImobiliario,
  );
  if (!semVinculoNoImobiliario) return true;

  return options.showSemVinculos;
}

export function shouldIncludeVinculoEdificacaoRioBranco(
  vinculo: ImobiliarioEdificacao,
  _vinculos: ImobiliarioEdificacao[],
  options: { showDesativadas: boolean; showSemVinculos: boolean },
): boolean {
  if (isVinculoEdificacaoAtivo(vinculo)) return true;
  if (!isVinculoEdificacaoDesativado(vinculo)) return false;

  return options.showDesativadas;
}

export function ordenarVinculosEdificacaoPorStatus<T extends { id?: unknown; status?: unknown }>(vinculos: T[]): T[] {
  return [...vinculos].sort((a, b) => {
    const activeDiff = Number(isVinculoEdificacaoAtivo(b)) - Number(isVinculoEdificacaoAtivo(a));
    if (activeDiff !== 0) return activeDiff;
    return Number(b.id ?? 0) - Number(a.id ?? 0);
  });
}

function toComplementarNode(
  vinculo: ImobiliarioEdificacao,
  edificacaoMap: Map<string, AnyData>
): EdificacaoVinculoNode {
  return {
    vinculo,
    edificacao: edificacaoMap.get(String(vinculo.id_edificacao)),
  };
}

function naoVinculadaToNode(
  edificacao: AnyData,
  idImobiliario?: string | number,
): EdificacaoVinculoNode {
  return {
    vinculo: {
      id: 0,
      id_imobiliario: idImobiliario ?? 0,
      id_edificacao: edificacao.id,
    } as ImobiliarioEdificacao,
    edificacao,
  };
}

function toSubunidadeNode(
  vinculo: ImobiliarioEdificacao,
  edificacaoMap: Map<string, AnyData>
): EdificacaoSubunidadeNode {
  return {
    vinculo,
    edificacao: edificacaoMap.get(String(vinculo.id_edificacao)),
    inscricaoSubunidade: String(vinculo.inscricao_subunidade ?? ""),
    complementares: [],
  };
}

/**
 * Monta a árvore de edificações a partir dos vínculos `imobiliario_edificacao`.
 */
export function buildEdificacoesHierarchy(
  vinculos: ImobiliarioEdificacao[],
  edificacoes: AnyData[],
  options?: {
    visibleEdificacaoIds?: Set<string>;
    separateSemVinculos?: boolean;
    idImobiliario?: string | number;
    vinculosDoImobiliario?: ImobiliarioEdificacao[];
  }
): EdificacoesHierarchy {
  const edificacaoMap = new Map<string, AnyData>();
  edificacoes.forEach((item) => edificacaoMap.set(String(item.id), item));

  const visible = options?.visibleEdificacaoIds;
  const isVisible = (idEdificacao: number | string) => {
    const key = String(idEdificacao);
    if (!edificacaoMap.has(key)) return false;
    if (visible && !visible.has(key)) return false;
    return true;
  };

  const principals: ImobiliarioEdificacao[] = [];
  const subunidades: ImobiliarioEdificacao[] = [];
  const complementares: ImobiliarioEdificacao[] = [];
  const orphans: EdificacaoVinculoNode[] = [];

  ordenarVinculosEdificacaoPorStatus(vinculos).forEach((vinculo) => {
    if (!isVisible(vinculo.id_edificacao)) return;

    const tipo = normalizeTipoVinculo(vinculo.tipo_vinculo);
    if (tipo === TIPO_VINCULO_EDIFICACAO.PRINCIPAL) principals.push(vinculo);
    else if (tipo === TIPO_VINCULO_EDIFICACAO.SUBUNIDADE) subunidades.push(vinculo);
    else if (tipo === TIPO_VINCULO_EDIFICACAO.COMPLEMENTAR) complementares.push(vinculo);
    else orphans.push(toComplementarNode(vinculo, edificacaoMap));
  });

  const unidades: EdificacaoUnidadePrincipalNode[] = principals.map((vinculo) => ({
    vinculo,
    edificacao: edificacaoMap.get(String(vinculo.id_edificacao)),
    inscricaoUnidade: String(vinculo.inscricao_unidade ?? ""),
    subunidades: [],
    complementares: [],
  }));

  const unidadeByInscricao = new Map<string, EdificacaoUnidadePrincipalNode[]>();
  unidades.forEach((unidade) => {
    if (!isInscricaoPreenchida(unidade.inscricaoUnidade)) return;
    const lista = unidadeByInscricao.get(unidade.inscricaoUnidade) ?? [];
    lista.push(unidade);
    unidadeByInscricao.set(unidade.inscricaoUnidade, lista);
  });

  const subunidadeByInscricaoSub = new Map<string, EdificacaoSubunidadeNode>();

  subunidades.forEach((vinculo) => {
    const inscricaoUnidade = String(vinculo.inscricao_unidade ?? "");
    const candidatos = unidadeByInscricao.get(inscricaoUnidade);
    const node = toSubunidadeNode(vinculo, edificacaoMap);

    if (candidatos?.length) {
      candidatos[0].subunidades.push(node);
      if (isInscricaoPreenchida(node.inscricaoSubunidade)) {
        subunidadeByInscricaoSub.set(node.inscricaoSubunidade, node);
      }
      return;
    }

    orphans.push(node);
  });

  complementares.forEach((vinculo) => {
    const node = toComplementarNode(vinculo, edificacaoMap);
    const inscricaoUnidade = String(vinculo.inscricao_unidade ?? "");
    const inscricaoSubunidade = String(vinculo.inscricao_subunidade ?? "");

    if (isInscricaoPreenchida(inscricaoSubunidade)) {
      const parentSub = subunidadeByInscricaoSub.get(inscricaoSubunidade);
      if (parentSub) {
        parentSub.complementares.push(node);
        return;
      }
    }

    if (isInscricaoPreenchida(inscricaoUnidade)) {
      const candidatos = unidadeByInscricao.get(inscricaoUnidade);
      if (candidatos?.length) {
        candidatos[0].complementares.push(node);
        return;
      }
    }

    orphans.push(node);
  });

  const vinculosParaDetectarVinculo = options?.vinculosDoImobiliario ?? vinculos;
  const vinculadosIds = new Set(
    vinculosParaDetectarVinculo.map((v) => String(v.id_edificacao)),
  );
  const naoVinculadas = edificacoes.filter(
    (item) => isVisible(item.id) && !vinculadosIds.has(String(item.id)),
  );

  const semVinculos = options?.separateSemVinculos
    ? naoVinculadas.map((edificacao) => naoVinculadaToNode(edificacao, options.idImobiliario))
    : [];

  return {
    unidades,
    orphans,
    naoVinculadas: options?.separateSemVinculos ? [] : naoVinculadas,
    semVinculos,
  };
}
